/**
 * Datenmodell des Dialogs „Beteiligte zuordnen“ (Kundenfeedback 18.10.2026,
 * XII/XIII). Reine Hilfslogik ohne Vue-Abhängigkeit, damit Rollenliste,
 * Reiter, Favoriten-Gruppen und Aktenvorschläge testbar bleiben. Alle
 * Funktionen liefern neue Objekte.
 */
import type { Contact } from "@/services/contactModel";
import { type ContactSearchHit, contactToHit } from "@/utils/contactSearchHits";
import { type ParticipantLike, isNameAmongParticipants } from "@/utils/participantMatch";
import { PARTICIPANT_CATEGORIES, findCategory, matchesContactQuery, sortContactsForOverview } from "../../participantCategories";
import {
  type InCaseGroup,
  type InCaseMember,
  type InCaseParticipant,
  MANDANT_ROLE_KEY,
  buildInCaseGroups,
} from "../../components/addParticipantOverview";

export { MANDANT_ROLE_KEY };

/** Reiter der rechten Seite (Pillen). */
export type SearchTab = "all" | "favorites" | "own";

export interface SearchTabInfo {
  key: SearchTab;
  label: string;
  /** Erklärtext – erscheint als Tooltip beim Darüberfahren der Pille (Kundenfeedback 24.10.2026, XX) */
  hint: string;
}

export const SEARCH_TABS: readonly SearchTabInfo[] = [
  { key: "all", label: "Suche gesamt", hint: "Durchsucht Ihre eigenen Kontakte und alle in KARS registrierten Unternehmen." },
  { key: "favorites", label: "Favoriten", hint: "Ihre Standard-Partner – ein Klick ordnet sie direkt ihrer Standardrolle zu." },
  { key: "own", label: "Eigene Kontakte", hint: "Ihr gesamtes Adressbuch einschließlich der Favoriten." },
];

export interface RoleChoice {
  value: string;
  label: string;
}

/** Jede Rolle ist wählbar – Kunde zuerst, dann alle Fall-Rollen. */
export function roleChoices (): RoleChoice[] {
  return [
    { value: MANDANT_ROLE_KEY, label: "Kunde" },
    ...PARTICIPANT_CATEGORIES.map((c) => ({ value: c.role, label: c.label })),
  ];
}

export function roleLabel (role: string | null | undefined): string {
  if (!role) { return ""; }
  if (role === MANDANT_ROLE_KEY) { return "Kunde"; }
  return findCategory(role)?.label ?? role;
}

/** Gültige Rolle oder Rückfall „Weitere Kontakte“. */
export function normalizeRole (role: string | null | undefined): string {
  if (role === MANDANT_ROLE_KEY) { return role; }
  if (role && PARTICIPANT_CATEGORIES.some((c) => c.role === role)) { return role; }
  return PARTICIPANT_CATEGORIES.find((c) => c.catchAll)?.role ?? "Sonstige";
}

/**
 * Vorbelegung von „Zuordnen als …“: links gewählte Rolle, sonst Standardrolle
 * bzw. Kontakttyp – ein Adressbuch-Kontakt vom Typ „Kunde“ wird als Kunde
 * (Mandant) vorgeschlagen. Frei änderbar.
 */
export function presetRoleForHit (hit: Pick<ContactSearchHit, "defaultRole" | "favoriteRole" | "contact">, targetRole = ""): string {
  if (targetRole) { return normalizeRole(targetRole); }
  if (!hit.favoriteRole && hit.contact?.contactType === "Kunde") { return MANDANT_ROLE_KEY; }
  return normalizeRole(hit.defaultRole);
}

/** Adressbuch-Kontakttyp zu einer Fall-Rolle (für „In Kontakte übernehmen“). */
export function contactTypeForRole (role: string | null | undefined): string | null {
  if (role === MANDANT_ROLE_KEY) { return "Kunde"; }
  const cat = findCategory(role);
  if (!cat || cat.catchAll) { return "Sonstige"; }
  return cat.contactTypes[0] ?? "Sonstige";
}

/**
 * Linke Spalte: ruhige Rollenliste in fester Reihenfolge (wie vor #1120,
 * ohne Icons) – Kunde, dann alle Fall-Rollen; je Rolle die Beteiligten.
 */
export function buildRoleColumn (participants: readonly InCaseParticipant[], autoInsuranceCount = 0): InCaseGroup[] {
  const groups = buildInCaseGroups(participants, { autoInsuranceCount });
  const order = [MANDANT_ROLE_KEY, ...PARTICIPANT_CATEGORIES.map((c) => c.role)];
  // eslint-disable-next-line unicorn/no-array-sort -- Kopie wird sortiert
  return [...groups].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
}

/**
 * Kurzfassung einer Rolle für die linke Spalte (Kundenfeedback 24.10.2026,
 * XIX): die Namen der Zugeordneten – je Organisation nur einmal, damit z. B.
 * mehrere Sachbearbeiter derselben Kanzlei als „Polz & Kollegen“ erscheinen.
 */
export function roleSummary (group: Pick<InCaseGroup, "members" | "extraCount">): string {
  const names = [...new Set(group.members.map((m) => m.name))];
  const parts = [...names];
  if (group.extraCount > 0) {
    parts.push(`${group.extraCount} aus Falldaten erkannt`);
  }
  return parts.join(", ");
}

const INSURANCE_ROLES = new Set(["Versicherung", "Versicherungsmakler"]);

/**
 * Kann dieser Beteiligte direkt aus dem Dialog zur Akte eingeladen werden?
 * Nur externe Beteiligte mit E-Mail, die noch nicht eingeladen sind –
 * Versicherungen werden nie eingeladen.
 */
export function canInviteMember (member: Pick<InCaseMember, "participant">, roleKey: string): boolean {
  const p = member.participant;
  if (roleKey === MANDANT_ROLE_KEY || INSURANCE_ROLES.has(roleKey)) { return false; }
  if (p.status !== "external" || p.invitedAt || p.participationStatus === "pending") { return false; }
  if ((p.roles ?? []).some((r) => INSURANCE_ROLES.has(r))) { return false; }
  const id = Number(p.id);
  return Number.isFinite(id) && id > 0 && !!(p.email ?? "").trim();
}

/** Eigene Kontakte als Treffer, gefiltert nach Suchbegriff, Favoriten zuerst. */
export function ownContactHits (contacts: readonly Contact[], query: string): ContactSearchHit[] {
  const matching = contacts.filter((c) => matchesContactQuery(c, query));
  return sortContactsForOverview(matching).map((c) => contactToHit(c));
}

export interface FavoriteGroup {
  role: string;
  label: string;
  hits: ContactSearchHit[];
}

/** Favoriten nach Standardrolle gruppiert (Rollen-Reihenfolge, ohne Rolle zuletzt). */
export function favoriteGroups (contacts: readonly Contact[], query: string): FavoriteGroup[] {
  const hits = ownContactHits(contacts.filter((c) => c.isFavorite), query);
  const order = [MANDANT_ROLE_KEY, ...PARTICIPANT_CATEGORIES.map((c) => c.role)];
  const roles = [...new Set(hits.map((h) => normalizeRole(h.defaultRole)))];
  // eslint-disable-next-line unicorn/no-array-sort -- neue Liste
  return [...roles].sort((a, b) => order.indexOf(a) - order.indexOf(b)).map((role) => ({
    role,
    label: roleLabel(role),
    hits: hits.filter((h) => normalizeRole(h.defaultRole) === role),
  }));
}

/** Filter-Chips: „Alle“ plus die Rollen, die in den Treffern vorkommen. */
export function roleFilterChips (hits: readonly ContactSearchHit[]): RoleChoice[] {
  const present = new Set(hits.map((h) => normalizeRole(h.defaultRole)));
  const chips = roleChoices().filter((c) => present.has(c.value));
  return chips.length > 1 ? [{ value: "", label: "Alle" }, ...chips] : [];
}

export function filterHitsByRole (hits: readonly ContactSearchHit[], role: string): ContactSearchHit[] {
  if (!role) { return [...hits]; }
  return hits.filter((h) => normalizeRole(h.defaultRole) === role);
}

/** Schon aktiver Beteiligter dieser Akte (E-Mail-Abgleich)? */
export function hitAlreadyInCase (hit: Pick<ContactSearchHit, "email">, participants: readonly InCaseParticipant[]): boolean {
  const email = (hit.email ?? "").trim().toLowerCase();
  if (!email) { return false; }
  return participants.some((p) => !p.removedAt && (p.email ?? "").trim().toLowerCase() === email);
}

export interface CaseSuggestion {
  key: string;
  name: string;
  /** Vorgeschlagene Rolle (Sachverständiger bzw. Autohaus) */
  role: string;
  /** Herkunft in Klartext, z. B. „Gutachten“ */
  origin: string;
}

interface VehicleLike {
  expertAccordingToClient?: string | null;
  repairWorkshop?: string | null;
}

export interface CaseSuggestionSource {
  vehicles?: readonly VehicleLike[] | null;
  gutachtenSvName?: string | null;
}

/**
 * Vorschläge „Aus der Akte bekannt“ – dieselbe Datenquelle wie beim Termin
 * (ParticipantSuggestionHint / SectionTermine): Sachverständiger aus dem
 * Gutachten bzw. „lt. Kunde“, Werkstatt aus den Fahrzeugdaten. Namen, die
 * schon als Beteiligte in der Rolle geführt werden, entfallen.
 */
export function buildCaseSuggestions (source: CaseSuggestionSource, participants: readonly ParticipantLike[]): CaseSuggestion[] {
  const vehicles = source.vehicles ?? [];
  const raw: Omit<CaseSuggestion, "key">[] = [
    { name: source.gutachtenSvName ?? "", role: "Sachverständiger", origin: "Gutachten" },
    ...vehicles.map((v) => ({ name: v.expertAccordingToClient ?? "", role: "Sachverständiger", origin: "Sachverständiger lt. Kunde" })),
    ...vehicles.map((v) => ({ name: v.repairWorkshop ?? "", role: "Autohaus", origin: "Werkstatt lt. Fahrzeugdaten" })),
  ];
  const seen = new Set<string>();
  return raw
    .map((s) => ({ ...s, name: s.name.trim() }))
    .filter((s) => s.name.length >= 2)
    .filter((s) => {
      const id = `${s.role}|${s.name.toLowerCase()}`;
      if (seen.has(id)) { return false; }
      seen.add(id);
      return true;
    })
    .filter((s) => !isNameAmongParticipants(s.name, [...participants], s.role))
    .map((s) => ({ ...s, key: `case-${s.role}-${s.name.toLowerCase()}` }));
}

/** Vorschläge passend zum Suchbegriff (ohne Begriff: alle). */
export function filterCaseSuggestions (suggestions: readonly CaseSuggestion[], query: string): CaseSuggestion[] {
  const q = query.trim().toLowerCase();
  if (!q) { return [...suggestions]; }
  return suggestions.filter((s) => s.name.toLowerCase().includes(q) || q.includes(s.name.toLowerCase()));
}
